import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Clock, X, Check, Volume2, VolumeX, Sun, Smartphone, TestTube, Calendar as CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { API_URL } from '../services/apiConfig';
import { isNativeApp } from '../utils/platform';
import { isNativeAlarmAvailable, scheduleNativeSadhanaAlarms, cancelNativeSadhanaAlarms } from '../services/nativeAlarmBridge';

interface DateReminder {
  id: string;
  date: string; // YYYY-MM-DD format
  time: string; // HH:MM format
  message: string;
  enabled: boolean;
}

interface ReminderSettings {
  enabled: boolean;
  times: string[]; // Array of times in HH:MM format
  sound: boolean;
  vibrate: boolean;
  brahmaMuhurta: boolean; // Special 4-5 AM reminder
  customMessage: string;
  dateReminders: DateReminder[]; // Date-specific reminders
}

const DEFAULT_SETTINGS: ReminderSettings = {
  enabled: false,
  times: ['06:00', '18:00'],
  sound: true,
  vibrate: true,
  brahmaMuhurta: false,
  customMessage: 'राम नाम जपने का समय हो गया है 🙏',
  dateReminders: []
};

const REMINDER_SETTINGS_KEY = 'sadhana_reminder_settings';

export const DailyReminders: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<ReminderSettings>(DEFAULT_SETTINGS);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'default'>('default');
  const [testSent, setTestSent] = useState(false);
  const [activeTab, setActiveTab] = useState<'daily' | 'calendar'>('daily');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [newReminderTime, setNewReminderTime] = useState('09:00');
  const [newReminderMessage, setNewReminderMessage] = useState('');

  useEffect(() => {
    // Load saved settings
    const saved = localStorage.getItem(REMINDER_SETTINGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings({ ...DEFAULT_SETTINGS, ...parsed, dateReminders: parsed.dateReminders || [] });
    }
    
    // Check notification permission
    if (isNativeApp()) {
      setPermissionStatus('default');
      if (localStorage.getItem('fcm_token')) setPermissionStatus('granted');
    } else if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, [isOpen]);
  
  const requestPermission = async () => {
    if (isNativeApp()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          // Attach listener BEFORE register to avoid race condition
          PushNotifications.addListener('registration', (token) => {
            localStorage.setItem('fcm_token', token.value);
            setPermissionStatus('granted');
          });
          PushNotifications.addListener('registrationError', (err) => {
            console.error('Native push registration error:', err);
          });
          await PushNotifications.register();
        } else {
          setPermissionStatus('denied');
        }
      } catch (e) { console.error('Native push error:', e); }
      return;
    }

    if (!('Notification' in window)) {
      alert('आपका ब्राउज़र नोटिफिकेशन सपोर्ट नहीं करता');
      return;
    }
    
    const permission = await Notification.requestPermission();
    setPermissionStatus(permission);
    
    if (permission === 'granted') {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          console.log('Service Worker ready for notifications');
        } catch (err) {
          console.error('Service Worker error:', err);
        }
      }
    }
  };
  
  
  const saveSettings = async (newSettings: ReminderSettings) => {
    setSettings(newSettings);
    localStorage.setItem(REMINDER_SETTINGS_KEY, JSON.stringify(newSettings));
    
    // Also save to backend for server-side push notifications
    try {
      const sessionToken = localStorage.getItem('session_token');
      const fcmToken = localStorage.getItem('fcm_token');
      
      if (sessionToken) {
        const response = await fetch(`${API_URL}/api/reminders/save-settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`
          },
          credentials: 'include',
          body: JSON.stringify({
            enabled: newSettings.enabled,
            times: newSettings.times,
            sound: newSettings.sound,
            vibrate: newSettings.vibrate,
            brahma_muhurta: newSettings.brahmaMuhurta,
            custom_message: newSettings.customMessage,
            fcm_token: fcmToken
          })
        });
        
        if (response.ok) {
          console.log('[Reminder] Settings saved to backend for push notifications');
        } else {
          console.warn('[Reminder] Backend save failed, using local only');
        }
      }
    } catch (err) {
      console.error('[Reminder] Backend save error:', err);
    }
    
    // Still maintain local fallback
    if (newSettings.enabled && permissionStatus === 'granted') {
      scheduleReminders(newSettings);
    } else {
      cancelReminders();
    }
  };
  
  const scheduleReminders = async (settings: ReminderSettings) => {
    // Clear any existing scheduled notifications
    cancelReminders();
    
    if (!settings.enabled) return;
    
    const scheduledTimes = [...settings.times];
    if (settings.brahmaMuhurta) {
      scheduledTimes.push('04:30');
    }
    
    localStorage.setItem('scheduled_reminder_times', JSON.stringify(scheduledTimes));
    localStorage.setItem('reminder_message', settings.customMessage);
    localStorage.setItem('reminder_sound', settings.sound.toString());
    localStorage.setItem('reminder_vibrate', settings.vibrate.toString());
    
    // Use Native AlarmManager bridge (ForegroundService) in Capacitor
    if (isNativeAlarmAvailable()) {
      try {
        const soundName = settings.sound ? 'mantra_ram' : 'none';
        const result = scheduleNativeSadhanaAlarms(
          scheduledTimes,
          settings.customMessage,
          soundName,
          settings.vibrate
        );
        console.log('[Reminders] Native sadhana scheduled:', result);
        if (result.success) {
          console.log('[Reminders] Scheduled', result.alarmsScheduled, 'native alarms via ForegroundService');
        }
      } catch (e) {
        console.error('[Reminders] Native scheduling failed:', e);
        // Fallback to in-app check
        checkAndTriggerReminders(settings);
      }
    } else {
      // PWA fallback: periodic check
      checkAndTriggerReminders(settings);
    }
  };
  
  const cancelReminders = async () => {
    localStorage.removeItem('scheduled_reminder_times');
    if (isNativeAlarmAvailable()) {
      try {
        cancelNativeSadhanaAlarms();
        console.log('[Reminders] Native sadhana alarms cancelled');
      } catch {}
    }
  };
  
  const checkAndTriggerReminders = (settings: ReminderSettings) => {
    // This runs in the main thread - for production, use service worker
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const times = [...settings.times];
    if (settings.brahmaMuhurta) times.push('04:30');
    
    // Store last check time to avoid duplicate notifications
    const lastCheck = localStorage.getItem('last_reminder_check');
    if (lastCheck === currentTime) return;
    
    if (times.includes(currentTime)) {
      showNotification(settings.customMessage, settings.sound, settings.vibrate);
      localStorage.setItem('last_reminder_check', currentTime);
    }
  };
  
  const showNotification = async (message: string, sound: boolean, vibrate: boolean) => {
    if (vibrate && 'vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
    
    try {
      if (isNativeAlarmAvailable()) {
        // Use native bridge for immediate test notification
        const { showNativeNotification } = await import('../services/nativeAlarmBridge');
        showNativeNotification('साधना स्मरण', message);
      } else if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const registration = await navigator.serviceWorker.ready;
        const options: NotificationOptions = {
          body: message,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-72x72.png',
          tag: 'sadhana-reminder',
          requireInteraction: true,
          silent: !sound,
        };
        await registration.showNotification('🙏 जाप का समय', options);
      } else if ('Notification' in window) {
        new Notification('🙏 जाप का समय', { body: message });
      }
    } catch (err) {
      console.error('Notification error:', err);
    }
  };
  
  const sendTestNotification = async () => {
    if (permissionStatus !== 'granted') {
      await requestPermission();
      return;
    }
    
    showNotification(settings.customMessage, settings.sound, settings.vibrate);
    setTestSent(true);
    setTimeout(() => setTestSent(false), 3000);
  };
  
  const addTime = () => {
    if (settings.times.length >= 5) return;
    const newTimes = [...settings.times, '12:00'];
    saveSettings({ ...settings, times: newTimes });
  };
  
  const removeTime = (index: number) => {
    const newTimes = settings.times.filter((_, i) => i !== index);
    saveSettings({ ...settings, times: newTimes });
  };
  
  const updateTime = (index: number, value: string) => {
    const newTimes = [...settings.times];
    newTimes[index] = value;
    saveSettings({ ...settings, times: newTimes });
  };

  // Date-specific reminder functions
  const addDateReminder = () => {
    if (!selectedDate) return;
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    const newReminder: DateReminder = {
      id: `date_${Date.now()}`,
      date: dateStr,
      time: newReminderTime,
      message: newReminderMessage || `${formatDateHindi(selectedDate)} के लिए साधना रिमाइंडर 🙏`,
      enabled: true
    };
    
    const updatedReminders = [...(settings.dateReminders || []), newReminder];
    saveSettings({ ...settings, dateReminders: updatedReminders });
    
    // Reset form
    setNewReminderMessage('');
    setSelectedDate(undefined);
  };

  const removeDateReminder = (id: string) => {
    const updatedReminders = (settings.dateReminders || []).filter(r => r.id !== id);
    saveSettings({ ...settings, dateReminders: updatedReminders });
  };

  const toggleDateReminder = (id: string) => {
    const updatedReminders = (settings.dateReminders || []).map(r => 
      r.id === id ? { ...r, enabled: !r.enabled } : r
    );
    saveSettings({ ...settings, dateReminders: updatedReminders });
  };

  const formatDateHindi = (date: Date) => {
    const months = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  };

  const getRemindersForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (settings.dateReminders || []).filter(r => r.date === dateStr);
  };

  const hasReminderOnDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (settings.dateReminders || []).some(r => r.date === dateStr);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="reminders-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-md max-h-[90vh] bg-white dark:bg-stone-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-5 text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20">
            <X size={20} className="text-white" />
          </button>
          <Bell className="mx-auto mb-2 text-white" size={32} />
          <h2 className="text-xl font-bold text-white">साधना रिमाइंडर</h2>
          <p className="text-white/80 text-sm">Daily Sadhana Reminders</p>
        </div>
        
        {/* Permission Banner */}
        {permissionStatus !== 'granted' && (
          <div className="bg-amber-50 dark:bg-amber-900/30 p-3 border-b border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2">
              <BellOff size={16} className="text-amber-500" />
              <p className="text-xs text-amber-700 dark:text-amber-300 flex-1">
                नोटिफिकेशन अनुमति आवश्यक
              </p>
              <button
                onClick={requestPermission}
                className="px-2 py-1 bg-amber-500 text-white text-xs rounded-lg font-medium"
              >
                अनुमति दें
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-stone-700">
          <button
            onClick={() => setActiveTab('daily')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'daily'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <Clock size={16} /> दैनिक
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'calendar'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            <CalendarIcon size={16} /> कैलेंडर
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'daily' && (
            <>
          {/* Master Toggle */}
          <div className="bg-gray-50 dark:bg-stone-800 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.enabled ? (
                <Bell size={24} className="text-blue-500" />
              ) : (
                <BellOff size={24} className="text-gray-400" />
              )}
              <div>
                <p className="font-medium text-gray-800 dark:text-white">रिमाइंडर</p>
                <p className="text-xs text-gray-500">{settings.enabled ? 'चालू' : 'बंद'}</p>
              </div>
            </div>
            <button
              onClick={() => saveSettings({ ...settings, enabled: !settings.enabled })}
              className={`w-14 h-8 rounded-full transition-colors ${
                settings.enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-stone-600'
              }`}
              data-testid="reminder-toggle"
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-md transition-transform mx-1 ${
                settings.enabled ? 'translate-x-6' : 'translate-x-0'
              }`} />
            </button>
          </div>
          
          {/* Brahma Muhurta Special */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Sun size={20} className="text-purple-500" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">ब्रह्म मुहूर्त</p>
                  <p className="text-xs text-gray-500">4:30 AM - सर्वोत्तम समय</p>
                </div>
              </div>
              <button
                onClick={() => saveSettings({ ...settings, brahmaMuhurta: !settings.brahmaMuhurta })}
                className={`w-12 h-7 rounded-full transition-colors ${
                  settings.brahmaMuhurta ? 'bg-purple-500' : 'bg-gray-300 dark:bg-stone-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mx-1 ${
                  settings.brahmaMuhurta ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
          
          {/* Custom Times */}
          <div className="bg-white dark:bg-stone-800 rounded-xl p-4 border border-gray-100 dark:border-stone-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-blue-500" />
                <span className="font-medium text-gray-800 dark:text-white">रिमाइंडर समय</span>
              </div>
              {settings.times.length < 5 && (
                <button
                  onClick={addTime}
                  className="text-sm text-blue-500 font-medium"
                >
                  + जोड़ें
                </button>
              )}
            </div>
            
            <div className="space-y-2">
              {settings.times.map((time, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => updateTime(index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-stone-700 rounded-lg border border-gray-200 dark:border-stone-600 text-gray-800 dark:text-white"
                  />
                  {settings.times.length > 1 && (
                    <button
                      onClick={() => removeTime(index)}
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Sound & Vibration */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => saveSettings({ ...settings, sound: !settings.sound })}
              className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-colors ${
                settings.sound 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                  : 'bg-gray-50 dark:bg-stone-800 border-gray-200 dark:border-stone-700'
              }`}
            >
              {settings.sound ? (
                <Volume2 size={24} className="text-blue-500" />
              ) : (
                <VolumeX size={24} className="text-gray-400" />
              )}
              <span className={`text-sm font-medium ${settings.sound ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
                ध्वनि
              </span>
            </button>
            
            <button
              onClick={() => saveSettings({ ...settings, vibrate: !settings.vibrate })}
              className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-colors ${
                settings.vibrate 
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                  : 'bg-gray-50 dark:bg-stone-800 border-gray-200 dark:border-stone-700'
              }`}
            >
              <Smartphone size={24} className={settings.vibrate ? 'text-blue-500' : 'text-gray-400'} />
              <span className={`text-sm font-medium ${settings.vibrate ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
                कंपन
              </span>
            </button>
          </div>
          
          {/* Custom Message */}
          <div className="bg-white dark:bg-stone-800 rounded-xl p-4 border border-gray-100 dark:border-stone-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              संदेश
            </label>
            <textarea
              value={settings.customMessage}
              onChange={(e) => setSettings({ ...settings, customMessage: e.target.value })}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-stone-700 rounded-lg border border-gray-200 dark:border-stone-600 text-gray-800 dark:text-white resize-none"
              rows={2}
              placeholder="रिमाइंडर संदेश..."
            />
          </div>
          
          {/* Save Button */}
          {settings.enabled && settings.times.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 text-center">
              <p className="text-green-700 dark:text-green-400 text-sm font-medium">
                ✅ रिमाइंडर सेट है: {settings.times.join(', ')}
                {settings.brahmaMuhurta && ', 04:30'}
              </p>
              <p className="text-green-600 dark:text-green-500 text-xs mt-1">
                {isNativeAlarmAvailable() ? '✅ ForegroundService — Background में भी notification + audio आएगी' : isNativeApp() ? '⚠️ Native bridge उपलब्ध नहीं' : '⚠️ App खुला रहना चाहिए'}
              </p>
            </div>
          )}
          
          <button
            onClick={() => {
              if (permissionStatus !== 'granted') {
                requestPermission();
                return;
              }
              saveSettings({ ...settings, enabled: true });
              alert(`✅ रिमाइंडर सेट हो गया!\n\nसमय: ${settings.times.join(', ')}${settings.brahmaMuhurta ? ', 04:30 (ब्रह्म मुहूर्त)' : ''}${isNativeAlarmAvailable() ? '\n\n✅ ForegroundService — Background में भी notification + audio आएगी' : '\n\n⚠️ Note: App खुला रहना चाहिए notification के लिए'}`);
            }}
            className="w-full py-4 rounded-xl flex items-center justify-center gap-2 font-bold text-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg hover:shadow-xl transition-all"
            data-testid="save-reminder-btn"
          >
            <Check size={24} /> रिमाइंडर सेट करें
          </button>

          {/* Quick Test - 1 minute reminder */}
          <button
            onClick={() => {
              if (permissionStatus !== 'granted') {
                requestPermission();
                return;
              }
              const now = new Date();
              now.setMinutes(now.getMinutes() + 1);
              const nextMinute = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
              const newTimes = [nextMinute];
              saveSettings({ ...settings, times: newTimes, enabled: true });
              alert(`⏰ 1 मिनट बाद notification आएगी!\n\nसमय: ${nextMinute}\n\nApp खुला रखें और wait करें...`);
            }}
            className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
            data-testid="quick-test-btn"
          >
            <Clock size={18} /> 1 मिनट बाद टेस्ट करें
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200 dark:bg-stone-700"></div>
            <span className="text-xs text-gray-400">या</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-stone-700"></div>
          </div>
          
          {/* Test Notification */}
          <button
            onClick={sendTestNotification}
            disabled={permissionStatus !== 'granted'}
            className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-colors ${
              permissionStatus === 'granted'
                ? 'bg-gray-100 dark:bg-stone-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-stone-700'
                : 'bg-gray-200 dark:bg-stone-700 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="test-notification-btn"
          >
            {testSent ? (
              <>
                <Check size={20} /> भेजा गया!
              </>
            ) : (
              <>
                <TestTube size={20} /> टेस्ट नोटिफिकेशन भेजें
              </>
            )}
          </button>
            </>
          )}

          {/* Calendar Tab Content */}
          {activeTab === 'calendar' && (
            <>
              {/* Calendar Picker */}
              <div className="bg-white dark:bg-stone-800 rounded-xl border border-gray-100 dark:border-stone-700 overflow-hidden">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date: Date | undefined) => setSelectedDate(date)}
                  className="mx-auto"
                  classNames={{}}
                  disabled={(date: Date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  data-testid="calendar-picker"
                />
              </div>

              {/* Selected Date Form */}
              {selectedDate && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <CalendarIcon size={18} className="text-blue-500" />
                    <span className="font-medium text-gray-800 dark:text-white">
                      {formatDateHindi(selectedDate)} के लिए रिमाइंडर
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={newReminderTime}
                      onChange={(e) => setNewReminderTime(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white dark:bg-stone-700 rounded-lg border border-gray-200 dark:border-stone-600 text-gray-800 dark:text-white text-sm"
                      data-testid="date-reminder-time"
                    />
                  </div>
                  
                  <input
                    type="text"
                    value={newReminderMessage}
                    onChange={(e) => setNewReminderMessage(e.target.value)}
                    placeholder="संदेश (वैकल्पिक)"
                    className="w-full px-3 py-2 bg-white dark:bg-stone-700 rounded-lg border border-gray-200 dark:border-stone-600 text-gray-800 dark:text-white text-sm"
                    data-testid="date-reminder-message"
                  />
                  
                  <button
                    onClick={addDateReminder}
                    className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                    data-testid="add-date-reminder-btn"
                  >
                    <Plus size={18} /> रिमाइंडर जोड़ें
                  </button>
                </div>
              )}

              {/* Existing Date Reminders List */}
              {settings.dateReminders && settings.dateReminders.length > 0 && (
                <div className="bg-white dark:bg-stone-800 rounded-xl border border-gray-100 dark:border-stone-700 p-4">
                  <h3 className="font-medium text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <CalendarIcon size={16} className="text-blue-500" />
                    आगामी रिमाइंडर ({settings.dateReminders.length})
                  </h3>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {settings.dateReminders
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((reminder) => {
                        const reminderDate = new Date(reminder.date + 'T00:00:00');
                        const isPast = reminderDate < new Date(new Date().setHours(0, 0, 0, 0));
                        
                        return (
                          <div
                            key={reminder.id}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              isPast 
                                ? 'bg-gray-100 dark:bg-stone-700 opacity-60' 
                                : 'bg-gray-50 dark:bg-stone-700/50'
                            }`}
                            data-testid={`date-reminder-${reminder.id}`}
                          >
                            <button
                              onClick={() => toggleDateReminder(reminder.id)}
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                reminder.enabled 
                                  ? 'bg-blue-500 border-blue-500' 
                                  : 'border-gray-300 dark:border-stone-500'
                              }`}
                            >
                              {reminder.enabled && <Check size={12} className="text-white" />}
                            </button>
                            
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                {formatDateHindi(reminderDate)} • {reminder.time}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {reminder.message}
                              </p>
                            </div>
                            
                            <button
                              onClick={() => removeDateReminder(reminder.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {(!settings.dateReminders || settings.dateReminders.length === 0) && !selectedDate && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <CalendarIcon size={40} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">कोई तारीख चुनकर रिमाइंडर जोड़ें</p>
                  <p className="text-xs mt-1">जैसे एकादशी, जयंती, आदि</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Mini card for showing in settings or dashboard
export const ReminderStatusCard: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [enabled, setEnabled] = useState(false);
  const [nextReminder, setNextReminder] = useState<string | null>(null);
  
  useEffect(() => {
    const saved = localStorage.getItem(REMINDER_SETTINGS_KEY);
    if (saved) {
      const settings: ReminderSettings = JSON.parse(saved);
      setEnabled(settings.enabled);
      
      if (settings.enabled && settings.times.length > 0) {
        // Find next reminder time
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        
        const times = [...settings.times];
        if (settings.brahmaMuhurta) times.push('04:30');
        
        const futureTime = times
          .map(t => {
            const [h, m] = t.split(':').map(Number);
            return { time: t, minutes: h * 60 + m };
          })
          .filter(t => t.minutes > currentMinutes)
          .sort((a, b) => a.minutes - b.minutes)[0];
        
        setNextReminder(futureTime?.time || times[0]);
      }
    }
  }, []);
  
  return (
    <button
      onClick={onClick}
      className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-4 w-full text-left hover:shadow-md transition-shadow"
      data-testid="reminder-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${enabled ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-stone-700'}`}>
            {enabled ? (
              <Bell size={20} className="text-blue-500" />
            ) : (
              <BellOff size={20} className="text-gray-400" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-200">साधना रिमाइंडर</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {enabled ? `अगला: ${nextReminder}` : 'बंद है'}
            </p>
          </div>
        </div>
        <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
      </div>
    </button>
  );
};

// Background reminder checker (call this in App.tsx)
export const startReminderChecker = () => {
  console.log('[Reminder] Starting reminder checker...');
  
  // On native with NativeAlarm bridge, alarms are handled by Android AlarmManager + ForegroundService
  // No need for web-based interval checking
  if (isNativeAlarmAvailable()) {
    console.log('[Reminder] Native AlarmManager + ForegroundService handles notifications');
    return () => {};
  }

  // Run check function (PWA/browser only)
  const runCheck = () => {
    const saved = localStorage.getItem(REMINDER_SETTINGS_KEY);
    if (!saved) return;
    
    const settings: ReminderSettings = JSON.parse(saved);
    if (!settings.enabled) return;
    
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    const times = [...settings.times];
    if (settings.brahmaMuhurta) times.push('04:30');
    
    const lastCheck = localStorage.getItem('last_reminder_check_time');
    
    // Skip if already checked this minute
    if (lastCheck === currentTime) return;
    
    if (times.includes(currentTime)) {
      console.log('[Reminder] TIME MATCH! Sending notification...');
      localStorage.setItem('last_reminder_check_time', currentTime);
      
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(registration => {
            registration.showNotification('जाप का समय', {
              body: settings.customMessage || 'राम नाम जपने का समय हो गया है',
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: 'sadhana-reminder-' + currentTime,
              requireInteraction: true,
              vibrate: settings.vibrate ? [200, 100, 200] : undefined,
            });
          });
        } else if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('जाप का समय', {
            body: settings.customMessage || 'राम नाम जपने का समय हो गया है',
            icon: '/logo192.png',
            tag: 'sadhana-reminder-' + currentTime,
            requireInteraction: true,
          });
        }
        
        if (settings.vibrate && 'vibrate' in navigator) {
          navigator.vibrate([200, 100, 200, 100, 200]);
        }
      } catch (err) {
        console.error('[Reminder] Notification error:', err);
      }
    }
  };
  
  // Run immediately on start
  runCheck();
  
  // Then check every 15 seconds for more accuracy
  const checkInterval = setInterval(runCheck, 15000);
  
  console.log('[Reminder] PWA checker started, interval: 15 seconds');
  
  return () => {
    console.log('[Reminder] Stopping checker');
    clearInterval(checkInterval);
  };
};

export default DailyReminders;
